import { Fragment, useEffect, useRef, useState } from "react";
import DateInput from "@/components/DateInput";
import api, { fmtMoney, fmtDate, todayISO } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Bank, Money, CreditCard, TrendUp, Plus, ArrowUp, ArrowDown,
  UploadSimple, Tag, Warning, CheckCircle, Trash, FileXls, PencilSimple, Prohibit, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CuentaCombobox from "@/components/CuentaCombobox";
import MovimientosAllTab from "@/pages/MovimientosAllTab";
import ConciliarMovimientoDialog, { ConciliadoBadge } from "@/components/ConciliarMovimientoDialog";
import ConvertirMovimientoDialog from "@/components/ConvertirMovimientoDialog";
import ConciliacionTab from "@/components/ConciliacionTab";
import ImportCalendar from "@/components/ImportCalendar";
import { CajaAcreditacionFields, ACREDITACION_DEFAULTS, normalizarAcreditacion } from "@/components/CajaAcreditacionFields";
import { ArqueoView } from "@/pages/pos/PosComponents";
import { Coin, Clock, ClockCounterClockwise, Export, LinkSimple, UserCircle, CaretLeft, CaretRight } from "@phosphor-icons/react";
import NumberInput from "@/components/ui/NumberInput";

const TIPOS_CAJA = [
  { v: "efectivo", l: "Efectivo", icon: Money },
  { v: "banco", l: "Banco", icon: Bank },
  { v: "digital", l: "Digital", icon: CreditCard },
  { v: "inversion", l: "Inversión", icon: TrendUp },
];

export default function CajasPage() {
  const [tab, setTab] = useState("cajas");
  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [arqueoOpen, setArqueoOpen] = useState(false);
  const [histArqueoOpen, setHistArqueoOpen] = useState(false);
  const [histArqueos, setHistArqueos] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [movs, setMovs] = useState([]);
  const [movPageSize, setMovPageSize] = useState(20);
  const [movPage, setMovPage] = useState(1);
  useEffect(() => { setMovPage(1); }, [movs, movPageSize, selected?.id]);
  const movTotalPages = Math.max(1, Math.ceil(movs.length / movPageSize));
  const movsPagina = movs.slice((movPage - 1) * movPageSize, movPage * movPageSize);
  const [rango, setRango] = useState({ desde: "", hasta: "" });
  const rangoParams = (r) => {
    const p = new URLSearchParams();
    if (r.desde) p.set("fecha_desde", r.desde);
    if (r.hasta) p.set("fecha_hasta", r.hasta);
    const s = p.toString();
    return s ? `?${s}` : "";
  };
  const [pendientes, setPendientes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openMov, setOpenMov] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rowCuenta, setRowCuenta] = useState({});
  const [confirm, setConfirm] = useState(null); // {type, target, title, message}
  const [dupPrompt, setDupPrompt] = useState(null); // {file, existing}
  const [previewData, setPreviewData] = useState(null); // {file, rows, num_columns, mapping, header_row}
  const fileRef = useRef(null);
  const [form, setForm] = useState({ nombre: "", tipo: "efectivo", moneda: "ARS", descripcion: "", cuenta_codigo: "", ...ACREDITACION_DEFAULTS });
  const [movForm, setMovForm] = useState({ fecha: todayISO(), tipo: "ingreso", concepto: "", monto: 0 });

  const load = async () => {
    const { data } = await api.get("/cajas");
    setCajas(data);
    if (selected) {
      const upd = data.find((c) => c.id === selected.id);
      setSelected(upd || null);
    }
  };
  const [sugerencias, setSugerencias] = useState({});
  const [selSug, setSelSug] = useState(new Set());
  const [reglas, setReglas] = useState([]);
  const [reglasOpen, setReglasOpen] = useState(false);
  const [concDesde, setConcDesde] = useState("");
  const guardarConcDesde = async (v) => {
    if (!v || v === concDesde) return;
    try {
      const { data } = await api.put("/conciliacion/config", { fecha_desde: v });
      setConcDesde(data.fecha_desde);
      toast.success(`Conciliación: se consideran cobros, pagos y gastos desde ${fmtDate(data.fecha_desde)}`);
      loadPendientes();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fecha inválida"); }
  };
  const loadReglas = async () => {
    try { const { data } = await api.get("/reglas-clasificacion"); setReglas(data || []); } catch (_) {}
  };
  const loadPendientes = async () => {
    const [{ data }, sugRes, cfgRes] = await Promise.all([
      api.get("/movimientos-caja/pendientes"),
      api.get("/conciliacion/sugerencias").catch(() => ({ data: {} })),
      api.get("/conciliacion/config").catch(() => ({ data: {} })),
    ]);
    setPendientes(data);
    setSugerencias(sugRes.data || {});
    if (cfgRes.data?.fecha_desde) setConcDesde(cfgRes.data.fecha_desde);
    setSelSug(new Set());
    // Prefill de cuenta por regla aprendida (sin pisar lo que eligió el usuario)
    setRowCuenta((prev) => {
      const next = { ...prev };
      data.forEach((m) => { if (m.cuenta_sugerida && !next[m.id]) next[m.id] = m.cuenta_sugerida.cuenta_codigo; });
      return next;
    });
    loadReglas();
  };
  const loadCuentas = async () => {
    const { data } = await api.get("/cuentas");
    setCuentas(data.filter((c) => c.imputable));
  };
  const loadBatches = async () => {
    const { data } = await api.get("/import-batches");
    setBatches(data);
  };

  useEffect(() => { load(); loadPendientes(); loadCuentas(); loadBatches(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (c, r = rango) => {
    setSelected(c);
    const { data } = await api.get(`/cajas/${c.id}/movimientos${rangoParams(r)}`);
    setMovs(data);
  };

  const aplicarRango = (next) => {
    setRango(next);
    if (selected) openDetail(selected, next);
  };

  const refreshAll = async () => {
    await load();
    if (selected) {
      const { data } = await api.get(`/cajas/${selected.id}/movimientos${rangoParams(rango)}`);
      setMovs(data);
    }
    loadPendientes();
    loadBatches();
  };

  const [editingCajaId, setEditingCajaId] = useState(null);

  const exportarDetalle = async () => {
    if (!selected) return;
    try {
      const res = await api.get(`/cajas/${selected.id}/movimientos/export${rangoParams(rango)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const suf = rango.desde || rango.hasta ? `_${rango.desde || "inicio"}_${rango.hasta || "hoy"}` : "";
      a.download = `detalle_caja_${(selected.nombre || "caja").replace(/[^\w]+/g, "_")}${suf}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Detalle exportado");
    } catch (e) {
      toast.error("No se pudo exportar el detalle");
    }
  };

  const openEditCaja = (c) => {
    setEditingCajaId(c.id);
    setForm({
      nombre: c.nombre || "",
      tipo: c.tipo || "efectivo",
      moneda: c.moneda || "ARS",
      descripcion: c.descripcion || "",
      cuenta_codigo: c.cuenta_codigo || "",
      plazo_acreditacion_dias: c.plazo_acreditacion_dias || 0,
      plazo_habiles: !!c.plazo_habiles,
      deducciones: (c.deducciones || []).map((d) => ({ ...d, cuenta_codigo: d.cuenta_codigo || "" })),
    });
    setOpenNew(true);
  };

  const openNewCaja = () => {
    setEditingCajaId(null);
    setForm({ nombre: "", tipo: "efectivo", moneda: "ARS", descripcion: "", cuenta_codigo: "", ...ACREDITACION_DEFAULTS });
    setOpenNew(true);
  };

  const createCaja = async (e) => {
    e.preventDefault();
    try {
      const payload = normalizarAcreditacion(form);
      if (editingCajaId) {
        await api.put(`/cajas/${editingCajaId}`, payload);
        toast.success("Caja actualizada");
      } else {
        await api.post("/cajas", payload);
        toast.success("Caja creada");
      }
      setOpenNew(false);
      setEditingCajaId(null);
      setForm({ nombre: "", tipo: "efectivo", moneda: "ARS", descripcion: "", cuenta_codigo: "", ...ACREDITACION_DEFAULTS });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const registrarMov = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cajas/movimiento", {
        caja_id: selected.id, fecha: movForm.fecha, tipo: movForm.tipo,
        concepto: movForm.concepto, monto: parseFloat(movForm.monto || 0),
      });
      toast.success("Movimiento registrado");
      setOpenMov(false);
      setMovForm({ fecha: todayISO(), tipo: "ingreso", concepto: "", monto: 0 });
      refreshAll();
    } catch { toast.error("Error"); }
  };

  const runImport = async (file, force = false, mapping = null) => {
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    if (mapping) {
      fd.append("col_fecha", String(mapping.fecha));
      fd.append("col_monto", String(mapping.monto));
      fd.append("col_desc", String(mapping.descripcion));
      if (mapping.numero !== null && mapping.numero !== undefined && mapping.numero >= 0) {
        fd.append("col_numero", String(mapping.numero));
      }
      fd.append("header_row", String(mapping.header_row ?? 0));
      fd.append("monto_egreso_negativo", "true");
    }
    try {
      const { data } = await api.post(
        `/cajas/${selected.id}/import-excel${force ? "?force=true" : ""}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (data.inserted === 0 && data.fechas_omitidas?.length) {
        toast.warning(`No se importó nada: las fechas ${data.fechas_omitidas.map(fmtDate).join(", ")} ya estaban subidas para esta caja`, { duration: 9000 });
      } else {
        toast.success(
          data.auto_clasificados
            ? `${data.inserted} movimientos importados · ${data.auto_clasificados} clasificados automáticamente por reglas`
            : `${data.inserted} movimientos importados`
        );
        if (data.fechas_omitidas?.length) {
          toast.warning(`${data.omitidos} movimiento(s) omitidos: las fechas ${data.fechas_omitidas.map(fmtDate).join(", ")} ya estaban subidas para esta caja`, { duration: 9000 });
        }
        if (data.omitidos_duplicados > 0) {
          toast.warning(`${data.omitidos_duplicados} movimiento(s) omitidos porque ya existían en la caja (mismo número, monto y fecha)`, { duration: 9000 });
        }
      }
      refreshAll();
      if (data.inserted > 0) setTab("pendientes");
      setDupPrompt(null);
      setPreviewData(null);
      return true;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.batch) {
        // Duplicate detected → prompt
        setDupPrompt({ file, existing: detail.batch, mapping });
        return false;
      }
      toast.error(typeof detail === "string" ? detail : "Error al importar");
      return false;
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    // 1) Pedimos preview al backend, luego el usuario mapea columnas.
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/cajas/${selected.id}/import-excel/preview`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreviewData({
        file,
        rows: data.rows || [],
        num_columns: data.num_columns || 0,
        mapping: {
          fecha: data.guess?.fecha ?? 0,
          descripcion: data.guess?.descripcion ?? 1,
          monto: data.guess?.monto ?? Math.max((data.num_columns || 1) - 1, 0),
          numero: data.guess?.numero ?? -1,
          header_row: data.header_row_default ?? 0,
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo leer el Excel");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmPreviewImport = async () => {
    if (!previewData) return;
    const { file, mapping } = previewData;
    // Validaciones básicas de mapeo
    if (
      mapping.fecha === mapping.monto ||
      mapping.fecha === mapping.descripcion ||
      mapping.monto === mapping.descripcion
    ) {
      return toast.error("Las columnas de Fecha, Monto y Descripción deben ser distintas.");
    }
    await runImport(file, false, mapping);
  };

  const confirmDuplicateUpload = async () => {
    if (!dupPrompt) return;
    await runImport(dupPrompt.file, true, dupPrompt.mapping || null);
  };

  const [rowObs, setRowObs] = useState({});

  const clasificar = async (mov_id) => {
    const code = rowCuenta[mov_id];
    if (!code) return toast.error("Seleccioná una cuenta contable");
    try {
      await api.patch(`/movimientos-caja/${mov_id}/clasificar`, { cuenta_codigo: code, observacion: rowObs[mov_id] || null });
      toast.success("Clasificado");
      setRowCuenta({ ...rowCuenta, [mov_id]: undefined });
      setRowObs({ ...rowObs, [mov_id]: undefined });
      loadPendientes();
    } catch { toast.error("Error"); }
  };

  const pendingWithCuenta = pendientes.filter((m) => rowCuenta[m.id]);

  // Filtros de "a clasificar": tipo (todos/ingreso/egreso) + caja
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [filtroCaja, setFiltroCaja] = useState("__all__");
  const [conciliarMov, setConciliarMov] = useState(null);
  const [convertirMov, setConvertirMov] = useState(null);
  const [conciliarMovs, setConciliarMovs] = useState(null);
  const [selGrupo, setSelGrupo] = useState(new Set());
  const toggleGrupo = (id) => setSelGrupo((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const abrirGrupo = () => {
    const movsSel = pendientes.filter((m) => selGrupo.has(m.id));
    if (movsSel.length < 2) return;
    const base = movsSel[0];
    if (movsSel.some((m) => m.caja_id !== base.caja_id || m.tipo !== base.tipo || (m.moneda || "ARS") !== (base.moneda || "ARS"))) {
      return toast.error("Para agruparlos tienen que ser de la misma caja, mismo tipo (ingreso/egreso) y moneda");
    }
    setConciliarMovs(movsSel);
  };
  const pendientesCaja = pendientes.filter((m) => filtroCaja === "__all__" || m.caja_id === filtroCaja);
  const countIng = pendientesCaja.filter((m) => m.tipo === "ingreso").length;
  const countEgr = pendientesCaja.length - countIng;
  const pendientesVisibles = pendientesCaja.filter((m) => filtroTipo === "todos" || m.tipo === filtroTipo);
  const cajasConPendientes = cajas.filter((c) => pendientes.some((m) => m.caja_id === c.id));

  const desconciliar = async (cid) => {
    if (!cid) return;
    try {
      await api.post(`/conciliacion/${cid}/desconciliar`);
      toast.success("Desconciliado: el movimiento del Excel volvió a pendientes");
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo desconciliar");
    }
  };

  const conciliarSugerido = async (m) => {
    const s = sugerencias[m.id];
    if (!s) return;
    try {
      await api.post("/conciliacion/asociar", { mov_id: m.id, cobro_pago_id: s.cobro_pago_id });
      toast.success(`Conciliado con ${s.tipo.startsWith("traspaso") ? "traspaso" : s.tipo} · ${s.contraparte_nombre}`);
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo conciliar");
    }
  };

  const sugeridosVisibles = pendientesVisibles.filter((m) => sugerencias[m.id]);
  const toggleSug = (id) => setSelSug((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleTodasSug = () => setSelSug((prev) =>
    prev.size === sugeridosVisibles.length ? new Set() : new Set(sugeridosVisibles.map((m) => m.id))
  );

  const conciliarLote = async () => {
    const items = [...selSug].filter((id) => sugerencias[id]).map((id) => ({ mov_id: id, cobro_pago_id: sugerencias[id].cobro_pago_id }));
    if (!items.length) return;
    try {
      const { data } = await api.post("/conciliacion/asociar-batch", { items });
      if (data.errores?.length) toast.warning(`${data.conciliados} conciliados · ${data.errores.length} con error`);
      else toast.success(`${data.conciliados} movimientos conciliados`);
      setConfirm(null);
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al conciliar en lote");
    }
  };

  const conReglaVisibles = pendientesVisibles.filter((m) => m.cuenta_sugerida);
  const aplicarReglas = async () => {
    try {
      const { data } = await api.post("/reglas-clasificacion/aplicar");
      toast.success(`${data.clasificados} movimientos clasificados por reglas`);
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al aplicar reglas");
    }
  };
  const borrarRegla = async (rid) => {
    try {
      await api.delete(`/reglas-clasificacion/${rid}`);
      toast.success("Regla eliminada");
      loadPendientes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };
  const [exclusiones, setExclusiones] = useState([]);
  const [nuevaExclusion, setNuevaExclusion] = useState("");
  const loadExclusiones = async () => {
    try { const { data } = await api.get("/reglas-clasificacion/exclusiones"); setExclusiones(data || []); } catch (_) {}
  };
  useEffect(() => { if (reglasOpen) loadExclusiones(); }, [reglasOpen]);
  const agregarExclusion = async (patron) => {
    const p = (patron ?? nuevaExclusion).trim();
    if (!p) return;
    try {
      const { data } = await api.post("/reglas-clasificacion/exclusiones", { patron: p });
      toast.success(data.reglas_borradas ? `"${data.patron}" no generará reglas · ${data.reglas_borradas} regla(s) existente(s) eliminada(s)` : `"${data.patron}" no generará reglas`);
      setNuevaExclusion("");
      loadExclusiones(); loadReglas(); loadPendientes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };
  const borrarExclusion = async (eid) => {
    try {
      await api.delete(`/reglas-clasificacion/exclusiones/${eid}`);
      loadExclusiones();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const clasificarTodos = async () => {
    if (pendingWithCuenta.length === 0) return;
    try {
      const { data } = await api.post("/movimientos-caja/clasificar-batch", {
        items: pendingWithCuenta.map((m) => ({ mov_id: m.id, cuenta_codigo: rowCuenta[m.id], observacion: rowObs[m.id] || null })),
      });
      toast.success(`${data.updated} movimientos clasificados`);
      setRowCuenta({});
      setRowObs({});
      setConfirm(null);
      loadPendientes();
      loadBatches();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const doConfirm = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === "delete-caja") {
        await api.delete(`/cajas/${confirm.target.id}`);
        toast.success("Caja eliminada");
        if (selected?.id === confirm.target.id) setSelected(null);
      } else if (confirm.type === "delete-mov") {
        await api.delete(`/movimientos-caja/${confirm.target.id}`);
        toast.success("Movimiento eliminado");
      } else if (confirm.type === "delete-batch") {
        const { data } = await api.delete(`/import-batches/${confirm.target.id}`);
        toast.success(`Lote eliminado (${data.deleted} movimientos)`);
      } else if (confirm.type === "clasificar-todos") {
        await clasificarTodos();
        return;
      } else if (confirm.type === "conciliar-lote") {
        await conciliarLote();
        return;
      }
      setConfirm(null);
      refreshAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <>
      <Topbar
        title="Cajas y Bancos"
        subtitle="Efectivo, bancos, cuentas digitales e inversiones."
        right={
          <div className="flex items-center gap-2">
            {selected && tab === "cajas" && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  data-testid="import-excel-input"
                  onChange={handleImport}
                />
                <Button
                  data-testid="import-excel-btn"
                  disabled={importing}
                  onClick={() => fileRef.current?.click()}
                  variant="outline"
                  className="rounded-sm h-9 border-[#6B1F24] text-[#6B1F24] hover:bg-[#F5F1E9]"
                >
                  <UploadSimple size={16} className="mr-1" />
                  {importing ? "Importando…" : "Importar Excel"}
                </Button>
              </>
            )}
            <Sheet open={openNew} onOpenChange={(v) => { setOpenNew(v); if (!v) setEditingCajaId(null); }}>
              <SheetTrigger asChild>
                <Button
                  data-testid="new-caja-btn"
                  onClick={openNewCaja}
                  className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9"
                >
                  <Plus size={16} className="mr-1" /> Nueva caja
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:w-[420px] sm:max-w-[420px]">
                <SheetHeader><SheetTitle className="font-heading">{editingCajaId ? "Editar Caja / Cuenta" : "Nueva Caja / Cuenta"}</SheetTitle></SheetHeader>
                <form onSubmit={createCaja} className="space-y-3 mt-6">
                  <div><Label className="text-[12px]">Nombre</Label>
                    <Input data-testid="caja-nombre" required className="rounded-sm mt-1"
                      value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
                  </div>
                  <div><Label className="text-[12px]">Tipo</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPOS_CAJA.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[12px]">Moneda</Label>
                    <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ARS">ARS</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[12px]">Descripción</Label>
                    <Input className="rounded-sm mt-1" value={form.descripcion}
                      onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[12px]">Cuenta contable del activo *</Label>
                    <div className="mt-1">
                      <CuentaCombobox
                        value={form.cuenta_codigo}
                        onChange={(codigo) => setForm({ ...form, cuenta_codigo: codigo })}
                        cuentas={cuentas}
                        placeholder="Elegí la cuenta del activo (ej: Banco Galicia CC)…"
                        testid="caja-cuenta-combobox"
                      />
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-1">
                      Necesario para generar asientos automáticos en cobros y pagos.
                    </div>
                  </div>
                  {!["cheques", "cheques_emitidos"].includes(form.tipo) && (
                    <CajaAcreditacionFields form={form} setForm={setForm} cuentas={cuentas} />
                  )}
                  <Button type="submit" className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]">Guardar</Button>
                </form>
              </SheetContent>
            </Sheet>
          </div>
        }
      />

      <div className="p-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 bg-transparent p-0 border-b border-[#E5E5E5] w-full justify-start rounded-none h-auto">
            <TabsTrigger
              data-testid="tab-cajas" value="cajas"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#6B1F24] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-[13px]"
            >Cajas y Bancos</TabsTrigger>
            <TabsTrigger
              data-testid="tab-pendientes" value="pendientes"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#6B1F24] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-[13px] flex items-center gap-2"
            >
              Conciliación
              {pendientes.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-sm bg-[#6B1F24] text-white text-[11px] font-mono">
                  {pendientes.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-historial" value="historial"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#6B1F24] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-[13px]"
            >Historial de importaciones</TabsTrigger>
            <TabsTrigger
              data-testid="tab-todos" value="todos"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#6B1F24] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-[13px]"
            >Todos los movimientos</TabsTrigger>
          </TabsList>

          {/* TAB CAJAS */}
          <TabsContent value="cajas" className="mt-0 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {cajas.map((c) => {
                const tinfo = TIPOS_CAJA.find((t) => t.v === c.tipo);
                const Icon = tinfo?.icon || Money;
                return (
                  <div key={c.id} className={`relative group border rounded-sm bg-white transition-colors duration-150 ${
                    selected?.id === c.id ? "border-[#6B1F24] ring-1 ring-[#6B1F24]" : "border-[#E5E5E5] hover:border-neutral-400"
                  }`}>
                    <button onClick={() => openDetail(c)} data-testid={`caja-card-${c.id}`}
                      className="w-full text-left p-4">
                      <div className="flex justify-between items-start">
                        <div className="w-8 h-8 rounded-sm bg-[#F5F1E9] text-[#6B1F24] flex items-center justify-center">
                          <Icon size={16} weight="bold" />
                        </div>
                        <span className="text-[10px] uppercase font-mono text-[#666] tracking-widest">{c.moneda}</span>
                      </div>
                      <div className="mt-3 font-medium text-[14px]">{c.nombre}</div>
                      <div className="text-[11px] text-[#666] capitalize">{c.tipo}</div>
                      <div className="mt-3 font-mono text-lg font-semibold">{fmtMoney(c.saldo, c.moneda)}</div>
                      {Number(c.pendiente_acreditacion) > 0 && (
                        <div className="mt-1 text-[11px] font-mono" data-testid={`caja-acred-${c.id}`}>
                          <span className="text-emerald-700">Disp. {fmtMoney(c.disponible, c.moneda)}</span>
                          <span className="text-amber-700 ml-2" title={(c.proximas_acreditaciones || []).map((a) => `${fmtDate(a.fecha_acreditacion)} · ${a.contraparte_nombre} · ${fmtMoney(a.monto, a.moneda)}`).join("\n")}>
                            A acreditar {fmtMoney(c.pendiente_acreditacion, c.moneda)}
                            {c.proximas_acreditaciones?.[0] ? ` (próx. ${fmtDate(c.proximas_acreditaciones[0].fecha_acreditacion)})` : ""}
                          </span>
                        </div>
                      )}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditCaja(c); }}
                      data-testid={`edit-caja-card-${c.id}`}
                      className="absolute top-2 right-9 p-1.5 rounded-sm text-[#999] hover:text-[#6B1F24] hover:bg-[#F5F1E9] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      title="Editar caja / asignar cuenta contable"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirm({ type: "delete-caja", target: c, title: "Eliminar caja", message: `¿Eliminar "${c.nombre}"? Solo se puede si no tiene movimientos ni cobros/pagos.` }); }}
                      data-testid={`del-caja-${c.id}`}
                      className="absolute top-2 right-2 p-1.5 rounded-sm text-[#999] hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      title="Eliminar caja"
                    >
                      <Trash size={14} />
                    </button>
                    {!c.cuenta_codigo && (
                      <div className="absolute bottom-2 right-2 flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm">
                        Sin cta. contable
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <ImportCalendar refreshKey={batches?.length} />

            {selected && (
              <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
                <div className="px-5 py-3 border-b flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-semibold">{selected.nombre}</div>
                    <div className="text-[11px] text-[#666] font-mono uppercase tracking-widest">{selected.tipo} · {selected.moneda}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-[11px] text-neutral-500 font-mono uppercase tracking-widest">Cuenta contable:</span>
                      <div className="min-w-[260px] flex-1 max-w-md">
                        <CuentaCombobox
                          value={selected.cuenta_codigo || ""}
                          onChange={async (codigo) => {
                            try {
                              await api.put(`/cajas/${selected.id}`, {
                                nombre: selected.nombre,
                                tipo: selected.tipo,
                                moneda: selected.moneda,
                                descripcion: selected.descripcion || "",
                                cuenta_codigo: codigo,
                              });
                              toast.success("Cuenta contable asignada");
                              await refreshAll();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Error");
                            }
                          }}
                          cuentas={cuentas}
                          placeholder="Sin cuenta asignada — elegí una…"
                          testid={`caja-cuenta-${selected.id}`}
                          compact
                        />
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="rango-fechas-caja">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#888]">Período:</span>
                        <DateInput className="rounded-sm h-8 w-32 font-mono text-[12px]" value={rango.desde}
                          onChange={(e) => aplicarRango({ ...rango, desde: e.target.value })}
                          placeholder="Desde" data-testid="rango-desde" />
                        <span className="text-[#999]">→</span>
                        <DateInput className="rounded-sm h-8 w-32 font-mono text-[12px]" value={rango.hasta}
                          onChange={(e) => aplicarRango({ ...rango, hasta: e.target.value })}
                          placeholder="Hasta" data-testid="rango-hasta" />
                        {[
                          { k: "mes", label: "Este mes" },
                          { k: "mes_ant", label: "Mes anterior" },
                          { k: "30", label: "Últimos 30 días" },
                        ].map((p) => (
                          <button key={p.k} type="button" data-testid={`rango-preset-${p.k}`}
                            onClick={() => {
                              const iso = (d) => d.toISOString().slice(0, 10);
                              const hoy = new Date();
                              let desde, hasta = iso(hoy);
                              if (p.k === "mes") desde = iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
                              else if (p.k === "mes_ant") {
                                desde = iso(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1));
                                hasta = iso(new Date(hoy.getFullYear(), hoy.getMonth(), 0));
                              } else desde = iso(new Date(hoy.getTime() - 30 * 86400000));
                              aplicarRango({ desde, hasta });
                            }}
                            className="text-[11px] font-mono px-2 py-1 rounded-sm border border-neutral-300 bg-white hover:bg-neutral-50">
                            {p.label}
                          </button>
                        ))}
                        {(rango.desde || rango.hasta) && (
                          <button type="button" data-testid="rango-limpiar" onClick={() => aplicarRango({ desde: "", hasta: "" })}
                            className="text-[11px] font-mono px-2 py-1 rounded-sm border border-[#6B1F24] text-[#6B1F24] hover:bg-[#F5F1E9]">
                            Todo el histórico
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selected.tipo === "efectivo" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setArqueoOpen(true)}
                          className="rounded-sm h-8 border-emerald-600 text-emerald-700 hover:bg-emerald-50 gap-1"
                          data-testid="arqueo-caja-btn"
                        >
                          <Coin size={14} weight="bold" /> ARQUEO
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setHistArqueoOpen(true)}
                          className="rounded-sm h-8 border-neutral-300 text-neutral-700 hover:bg-neutral-50 gap-1"
                          data-testid="hist-arqueo-btn"
                          title="Historial de arqueos"
                        >
                          <ClockCounterClockwise size={14} weight="bold" /> Historial
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={exportarDetalle}
                      className="rounded-sm h-8 border-neutral-300 text-neutral-700 hover:bg-neutral-50 gap-1"
                      data-testid="export-detalle-caja-btn"
                      title="Descargar el detalle en Excel con cortes por día y saldo de cierre"
                    >
                      <Export size={14} weight="bold" /> Exportar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditCaja(selected)}
                      className="rounded-sm h-8 border-[#6B1F24] text-[#6B1F24] hover:bg-[#F5F1E9]"
                      data-testid="edit-caja-btn"
                    >
                      Editar caja
                    </Button>
                    <Sheet open={openMov} onOpenChange={setOpenMov}>
                    <SheetTrigger asChild>
                      <Button data-testid="new-mov-btn" size="sm" className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-8">
                        <Plus size={14} className="mr-1" /> Movimiento manual
                      </Button>
                    </SheetTrigger>
                    <SheetContent className="w-full sm:w-[420px] sm:max-w-[420px]">
                      <SheetHeader><SheetTitle className="font-heading">Nuevo movimiento</SheetTitle></SheetHeader>
                      <form onSubmit={registrarMov} className="space-y-3 mt-6">
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label className="text-[12px]">Fecha</Label>
                            <DateInput className="rounded-sm mt-1 font-mono" value={movForm.fecha}
                              onChange={(e) => setMovForm({ ...movForm, fecha: e.target.value })} required />
                          </div>
                          <div><Label className="text-[12px]">Tipo</Label>
                            <Select value={movForm.tipo} onValueChange={(v) => setMovForm({ ...movForm, tipo: v })}>
                              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ingreso">Ingreso</SelectItem>
                                <SelectItem value="egreso">Egreso</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div><Label className="text-[12px]">Concepto</Label>
                          <Input required className="rounded-sm mt-1" value={movForm.concepto}
                            onChange={(e) => setMovForm({ ...movForm, concepto: e.target.value })} />
                        </div>
                        <div><Label className="text-[12px]">Monto</Label>
                          <NumberInput required step="0.01" className="rounded-sm mt-1 font-mono text-right"
                            value={movForm.monto} onChange={(e) => setMovForm({ ...movForm, monto: e.target.value })} />
                        </div>
                        <Button type="submit" className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]">Registrar</Button>
                      </form>
                    </SheetContent>
                  </Sheet>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px] grid-table">
                    <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50">
                      <tr>
                        <th className="px-5 py-2 text-left w-24">Fecha</th>
                        <th className="px-5 py-2 text-left w-28">Acreditación</th>
                        <th className="px-5 py-2 text-left w-24">Tipo</th>
                        <th className="px-5 py-2 text-left">Concepto</th>
                        <th className="px-5 py-2 text-left w-64">Cuenta contable</th>
                        <th className="px-5 py-2 text-right w-40">Monto</th>
                        <th className="px-5 py-2 text-right w-40">Saldo</th>
                        <th className="px-5 py-2 text-center w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {movsPagina.map((m, idx) => {
                        const prev = movsPagina[idx - 1];
                        const nuevoDia = !prev || prev.fecha !== m.fecha;
                        // Lista en orden descendente: la primera fila del día tiene el saldo al cierre.
                        const diaLabel = (() => {
                          const d = new Date(`${m.fecha}T00:00:00`);
                          if (isNaN(d)) return m.fecha;
                          const opts = { day: "numeric", month: "long" };
                          if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
                          const s = d.toLocaleDateString("es-AR", opts);
                          return s.charAt(0).toUpperCase() + s.slice(1);
                        })();
                        const diaRow = nuevoDia ? (
                          <tr key={`dia-${m.fecha}`} className="bg-neutral-50/80 border-t border-[#E5E5E5]" data-testid={`dia-row-${m.fecha}`}>
                            <td colSpan={6} className="px-5 py-1.5 text-[12px] font-semibold text-[#333]">{diaLabel}</td>
                            <td colSpan={2} className="px-5 py-1.5 text-right text-[12px] text-[#555]">
                              Saldo del día <span className="font-mono font-semibold text-[#1A1A1A]" data-testid={`saldo-dia-${m.fecha}`}>{m.saldo_hasta != null ? fmtMoney(m.saldo_hasta, m.moneda) : "—"}</span>
                            </td>
                          </tr>
                        ) : null;
                        // Direccion real del movimiento sobre el saldo de esta caja:
                        // - "ingreso" o "transferencia_ingreso" → suma (+)
                        // - resto (egreso, transferencia_egreso, arqueo negativo, etc.) → resta (-)
                        const esIngreso = m.tipo === "ingreso" || m.referencia_tipo === "transferencia_ingreso";
                        const labelTipo = m.es_deduccion
                          ? "comisión / deducción"
                          : m.referencia_tipo === "transferencia_ingreso"
                          ? "transferencia (ingreso)"
                          : m.referencia_tipo === "transferencia_egreso"
                          ? "transferencia (egreso)"
                          : m.tipo;
                        const hoy = new Date().toISOString().slice(0, 10);
                        const pendiente = m.acreditacion_diferida && m.fecha_acreditacion > hoy;
                        return (
                        <Fragment key={m.id}>
                        {diaRow}
                        <tr className={`group ${m.es_deduccion ? "bg-amber-50/40" : ""}`} data-testid={`mov-row-${m.id}`}>
                          <td className="px-5 py-2 font-mono">{fmtDate(m.fecha)}</td>
                          <td className="px-5 py-2 font-mono text-[12px]" data-testid={`mov-acred-${m.id}`}>
                            {m.acreditacion_diferida ? (
                              <span className={`inline-flex items-center gap-1 ${pendiente ? "text-amber-700" : "text-emerald-700"}`}
                                title={pendiente ? "Diferido: todavía no acreditó" : "Diferido: ya acreditó"}>
                                <Clock size={12} weight="fill" /> {fmtDate(m.fecha_acreditacion)}
                              </span>
                            ) : <span className="text-[#bbb]">—</span>}
                          </td>
                          <td className="px-5 py-2">
                            <span className={`inline-flex items-center gap-1 text-[11px] ${esIngreso ? "num-pos" : "num-neg"}`}>
                              {esIngreso ? <ArrowUp size={12} /> : <ArrowDown size={12} />} {labelTipo}
                            </span>
                          </td>
                          <td className="px-5 py-2">
                            {m.concepto}
                            {m.observacion && (
                              <div className="text-[11px] italic text-[#666] mt-0.5" data-testid={`mov-obs-${m.id}`}>“{m.observacion}”</div>
                            )}
                            {m.deducciones_total > 0 && (
                              <span className="ml-2 text-[10px] font-mono text-amber-700" title="Se descontaron comisiones/deducciones en movimientos aparte">
                                − {fmtMoney(m.deducciones_total, m.moneda)} deducc. · neto {fmtMoney(m.monto_neto_acreditado ?? m.monto - m.deducciones_total, m.moneda)}
                              </span>
                            )}
                            <ConciliadoBadge mov={m} onDesconciliar={desconciliar} />
                          </td>
                          <td className="px-5 py-2 font-mono text-[11px] text-[#666]">
                            {m.clasificado === false ? (
                              <span className="inline-flex items-center gap-1 text-amber-700">
                                <Warning size={12} weight="fill" /> Sin clasificar
                              </span>
                            ) : m.cuenta_codigo ? (
                              <span><span className="text-[#6B1F24]">{m.cuenta_codigo}</span> · {m.cuenta_nombre}</span>
                            ) : "-"}
                          </td>
                          <td className={`px-5 py-2 text-right font-mono ${esIngreso ? "num-pos" : "num-neg"}`}>
                            {esIngreso ? "+" : "-"} {fmtMoney(m.monto, m.moneda)}
                          </td>
                          <td
                            className="px-5 py-2 text-right font-mono text-[12px] text-[#333]"
                            data-testid={`saldo-hasta-${m.id}`}
                            title="Saldo de la caja hasta este movimiento (inclusive)"
                          >
                            {m.saldo_hasta != null ? fmtMoney(m.saldo_hasta, m.moneda) : "—"}
                          </td>
                          <td className="px-5 py-2 text-center">
                            <button
                              onClick={() => setConfirm({ type: "delete-mov", target: m, title: "Eliminar movimiento", message: `¿Eliminar "${m.concepto}" (${fmtMoney(m.monto, m.moneda)})? El saldo de la caja se revertirá.` })}
                              data-testid={`del-mov-${m.id}`}
                              className="p-1.5 rounded-sm text-[#999] hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                            >
                              <Trash size={13} />
                            </button>
                          </td>
                        </tr>
                        </Fragment>
                        );
                      })}
                      {movs.length === 0 && (
                        <tr><td colSpan={8} className="text-center py-8 text-[#999]">Sin movimientos.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {movs.length > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-2 border-t border-[#F0F0F0] text-[11px] font-mono text-[#666]" data-testid="movs-paginacion">
                    <div className="flex items-center gap-2">
                      <span>Mostrar</span>
                      {[20, 50].map((n) => (
                        <button key={n} type="button" onClick={() => setMovPageSize(n)}
                                className={`px-2 py-0.5 rounded-sm border ${movPageSize === n ? "bg-[#6B1F24] border-[#6B1F24] text-white" : "border-[#E5E5E5] hover:bg-neutral-50"}`}
                                data-testid={`movs-pagesize-${n}`}>
                          {n}
                        </button>
                      ))}
                      <span className="ml-2">{movs.length} movimiento{movs.length === 1 ? "" : "s"}</span>
                    </div>
                    {movTotalPages > 1 && (
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" className="h-7 px-2 rounded-sm" disabled={movPage <= 1}
                                onClick={() => setMovPage((p) => p - 1)} data-testid="movs-page-prev">
                          <CaretLeft size={12} weight="bold" />
                        </Button>
                        <span className="px-2" data-testid="movs-page-info">Página {movPage} de {movTotalPages}</span>
                        <Button variant="outline" size="sm" className="h-7 px-2 rounded-sm" disabled={movPage >= movTotalPages}
                                onClick={() => setMovPage((p) => p + 1)} data-testid="movs-page-next">
                          <CaretRight size={12} weight="bold" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* TAB PENDIENTES */}
          <TabsContent value="pendientes" className="mt-0">
            {tab === "pendientes" && <ConciliacionTab cajas={cajas} cuentas={cuentas} onChanged={() => { load(); loadPendientes(); }} />}
          </TabsContent>

          <TabsContent value="historial" className="mt-0">
            <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
              <div className="px-5 py-3 border-b bg-neutral-50">
                <div className="font-heading font-semibold text-[15px] flex items-center gap-2">
                  <FileXls size={16} weight="bold" className="text-[#6B1F24]" />
                  Historial de importaciones Excel
                </div>
                <div className="text-[11px] text-[#666] mt-0.5">
                  Cada carga masiva puede revertirse por completo; se eliminan los movimientos y el saldo se recalcula.
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px] grid-table">
                  <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50 border-t">
                    <tr>
                      <th className="px-5 py-2 text-left w-40">Fecha carga</th>
                      <th className="px-5 py-2 text-left">Archivo</th>
                      <th className="px-5 py-2 text-left w-40">Caja</th>
                      <th className="px-5 py-2 text-right w-24">Movs.</th>
                      <th className="px-5 py-2 text-right w-28">Clasificados</th>
                      <th className="px-5 py-2 text-right w-28">Pendientes</th>
                      <th className="px-5 py-2 text-right w-40">Delta caja</th>
                      <th className="px-5 py-2 text-center w-24">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.id} data-testid={`batch-${b.id}`} className="group">
                        <td className="px-5 py-2 font-mono text-[12px]">
                          {new Date(b.created_at).toLocaleString("es-AR")}
                        </td>
                        <td className="px-5 py-2 truncate max-w-md" title={b.filename}>{b.filename}</td>
                        <td className="px-5 py-2 text-[#666]">{b.caja_nombre}</td>
                        <td className="px-5 py-2 text-right font-mono">{b.count}</td>
                        <td className="px-5 py-2 text-right font-mono text-emerald-700">{b.clasificados}</td>
                        <td className="px-5 py-2 text-right font-mono text-amber-700">{b.pendientes}</td>
                        <td className={`px-5 py-2 text-right font-mono ${b.total_delta >= 0 ? "num-pos" : "num-neg"}`}>
                          {b.total_delta >= 0 ? "+" : ""}{fmtMoney(b.total_delta, b.moneda)}
                        </td>
                        <td className="px-5 py-2 text-center">
                          <button
                            onClick={() => setConfirm({
                              type: "delete-batch",
                              target: b,
                              title: "Eliminar lote de importación",
                              message: `¿Eliminar la carga "${b.filename}"? Se borrarán ${b.count} movimientos y el saldo de "${b.caja_nombre}" se revertirá en ${fmtMoney(b.total_delta, b.moneda)}.`,
                            })}
                            data-testid={`del-batch-${b.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] text-red-700 hover:bg-red-50 border border-red-200"
                          >
                            <Trash size={12} /> Revertir
                          </button>
                        </td>
                      </tr>
                    ))}
                    {batches.length === 0 && (
                      <tr><td colSpan={8} className="text-center py-12 text-[#999]">
                        Aún no hay importaciones de Excel.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* TAB TODOS */}
          <TabsContent value="todos" className="mt-0">
            <MovimientosAllTab cajas={cajas} cuentas={cuentas} />
          </TabsContent>
        </Tabs>
      </div>

      <ConvertirMovimientoDialog
        mov={convertirMov}
        onClose={() => setConvertirMov(null)}
        onDone={refreshAll}
      />

      <ConciliarMovimientoDialog
        mov={conciliarMov}
        movs={conciliarMovs}
        onClose={() => { setConciliarMov(null); setConciliarMovs(null); }}
        onDone={() => { setSelGrupo(new Set()); refreshAll(); }}
      />

      <Dialog open={reglasOpen} onOpenChange={setReglasOpen}>
        <DialogContent className="max-w-3xl rounded-sm p-0 overflow-hidden" data-testid="reglas-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <DialogTitle className="font-heading text-[16px] flex items-center gap-2">
              <Tag size={18} weight="bold" className="text-[#6B1F24]" /> Reglas de clasificación
            </DialogTitle>
            <div className="text-[12px] text-neutral-600 mt-1">
              Cada vez que clasificás un movimiento importado, el sistema recuerda la cuenta para ese concepto del banco (sin números) y la aplica sola en el próximo Excel.
            </div>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto">
            {reglas.length === 0 ? (
              <div className="py-10 text-center text-[13px] text-neutral-500" data-testid="reglas-vacio">Todavía no hay reglas. Clasificá un movimiento importado para crear la primera.</div>
            ) : (
              <table className="w-full text-[13px]">
                <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Concepto (patrón)</th>
                    <th className="px-4 py-2 text-left w-20">Tipo</th>
                    <th className="px-4 py-2 text-left w-64">Cuenta</th>
                    <th className="px-4 py-2 text-right w-24">Aplicada</th>
                    <th className="px-4 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {reglas.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-neutral-50 group" data-testid={`regla-${r.id}`}>
                      <td className="px-4 py-2">
                        <div className="font-mono text-[12px]">{r.clave}</div>
                        <div className="text-[10px] text-neutral-500 truncate max-w-[360px]" title={r.concepto_ejemplo}>ej: {r.concepto_ejemplo}</div>
                      </td>
                      <td className={`px-4 py-2 text-[11px] uppercase font-mono ${r.tipo === "ingreso" ? "num-pos" : "num-neg"}`}>{r.tipo}</td>
                      <td className="px-4 py-2 font-mono text-[11px]"><span className="text-[#6B1F24]">{r.cuenta_codigo}</span> · {r.cuenta_nombre}</td>
                      <td className="px-4 py-2 text-right font-mono text-[12px]">{r.aplicadas || 0}×</td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-0.5">
                          <button
                            onClick={() => agregarExclusion(r.clave)}
                            data-testid={`regla-excluir-${r.id}`}
                            className="p-1.5 rounded-sm text-[#999] hover:text-amber-700 hover:bg-amber-50"
                            title="Eliminar y no volver a aprender este concepto"
                          >
                            <Prohibit size={13} />
                          </button>
                          <button
                            onClick={() => borrarRegla(r.id)}
                            data-testid={`regla-del-${r.id}`}
                            className="p-1.5 rounded-sm text-[#999] hover:text-red-600 hover:bg-red-50"
                            title="Eliminar regla"
                          >
                            <Trash size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="border-t bg-neutral-50 px-5 py-4" data-testid="exclusiones-section">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-[#333]">
              <Prohibit size={14} weight="bold" className="text-amber-700" /> Conceptos que NO generan regla
            </div>
            <div className="text-[11px] text-neutral-600 mt-0.5 mb-2">
              Palabras genéricas que cambian en cada movimiento (ej. <span className="font-mono">PAGO</span> en resúmenes de MercadoPago, <span className="font-mono">RETIRO DE DINERO</span>). Si el concepto del banco las contiene, no se aprende ninguna regla.
            </div>
            <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); agregarExclusion(); }}>
              <Input
                data-testid="exclusion-input"
                value={nuevaExclusion}
                onChange={(e) => setNuevaExclusion(e.target.value)}
                placeholder="Ej: PAGO, TRANSFERENCIA, RETIRO DE DINERO"
                className="rounded-sm h-8 text-[12px] font-mono uppercase max-w-md"
              />
              <Button type="submit" size="sm" variant="outline" className="rounded-sm h-8 text-[11px]" data-testid="exclusion-add-btn" disabled={!nuevaExclusion.trim()}>
                <Plus size={13} className="mr-1" /> Agregar
              </Button>
            </form>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {exclusiones.length === 0 && <span className="text-[11px] text-neutral-400" data-testid="exclusiones-vacio">Sin exclusiones.</span>}
              {exclusiones.map((e) => (
                <span key={e.id} data-testid={`exclusion-${e.id}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-sm bg-white border border-amber-200 text-amber-900 font-mono text-[11px]">
                  {e.patron}
                  <button onClick={() => borrarExclusion(e.id)} data-testid={`exclusion-del-${e.id}`} className="p-0.5 rounded-sm hover:bg-amber-100" title="Quitar exclusión">
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-action-btn"
              onClick={doConfirm}
              className={`rounded-sm ${
                confirm?.type === "clasificar-todos"
                  ? "bg-[#0E793C] hover:bg-[#0a5c2d]"
                  : confirm?.type === "conciliar-lote"
                  ? "bg-[#6B1F24] hover:bg-[#571519]"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!dupPrompt} onOpenChange={(v) => { if (!v) { setDupPrompt(null); if (fileRef.current) fileRef.current.value = ""; } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-amber-600" />
              Archivo ya importado
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-[13px]">
                <p>
                  Este Excel ya fue subido anteriormente. Detalles del lote existente:
                </p>
                {dupPrompt && (
                  <div className="border border-[#E5E5E5] rounded-sm p-3 bg-neutral-50 text-[12px] font-mono space-y-1">
                    <div><span className="text-[#666]">Archivo:</span> {dupPrompt.existing.filename}</div>
                    <div><span className="text-[#666]">Caja:</span> {dupPrompt.existing.caja_nombre}</div>
                    <div><span className="text-[#666]">Cargado:</span> {new Date(dupPrompt.existing.created_at).toLocaleString("es-AR")}</div>
                    <div><span className="text-[#666]">Movimientos:</span> {dupPrompt.existing.count}</div>
                  </div>
                )}
                <p className="text-amber-700">
                  ¿Querés subirlo igual? Se creará un lote nuevo y los movimientos aparecerán duplicados en la caja.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-duplicate-upload"
              onClick={confirmDuplicateUpload}
              className="rounded-sm bg-amber-600 hover:bg-amber-700"
            >
              Subir de todas formas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---- Dialog Preview de Excel (mapeo de columnas) ---- */}
      <Dialog open={!!previewData} onOpenChange={(v) => { if (!v) setPreviewData(null); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileXls size={18} weight="fill" className="text-emerald-700" />
              Previsualizar e importar Excel
            </DialogTitle>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4">
              <div className="text-[12px] text-neutral-600">
                Mostramos las primeras {previewData.rows.length} filas. Elegí qué columna corresponde a cada campo antes de importar a <b>{selected?.nombre}</b>.
              </div>

              {/* Selectores de columnas */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Fila encabezado</Label>
                  <Select
                    value={String(previewData.mapping.header_row)}
                    onValueChange={(v) => setPreviewData({ ...previewData, mapping: { ...previewData.mapping, header_row: Number(v) } })}
                  >
                    <SelectTrigger className="rounded-sm h-9 text-[12px]" data-testid="preview-header-row"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-1">Sin encabezado</SelectItem>
                      {previewData.rows.map((_, idx) => (
                        <SelectItem key={idx} value={String(idx)}>Fila {idx + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Columna Fecha *</Label>
                  <Select
                    value={String(previewData.mapping.fecha)}
                    onValueChange={(v) => setPreviewData({ ...previewData, mapping: { ...previewData.mapping, fecha: Number(v) } })}
                  >
                    <SelectTrigger className="rounded-sm h-9 text-[12px]" data-testid="preview-col-fecha"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: previewData.num_columns }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>Columna {String.fromCharCode(65 + i)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Columna Monto *</Label>
                  <Select
                    value={String(previewData.mapping.monto)}
                    onValueChange={(v) => setPreviewData({ ...previewData, mapping: { ...previewData.mapping, monto: Number(v) } })}
                  >
                    <SelectTrigger className="rounded-sm h-9 text-[12px]" data-testid="preview-col-monto"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: previewData.num_columns }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>Columna {String.fromCharCode(65 + i)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Columna Descripción *</Label>
                  <Select
                    value={String(previewData.mapping.descripcion)}
                    onValueChange={(v) => setPreviewData({ ...previewData, mapping: { ...previewData.mapping, descripcion: Number(v) } })}
                  >
                    <SelectTrigger className="rounded-sm h-9 text-[12px]" data-testid="preview-col-desc"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: previewData.num_columns }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>Columna {String.fromCharCode(65 + i)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-mono text-neutral-500">Nº Movimiento (opcional)</Label>
                  <Select
                    value={String(previewData.mapping.numero ?? -1)}
                    onValueChange={(v) => setPreviewData({ ...previewData, mapping: { ...previewData.mapping, numero: Number(v) } })}
                  >
                    <SelectTrigger className="rounded-sm h-9 text-[12px]" data-testid="preview-col-numero"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-1">— sin usar —</SelectItem>
                      {Array.from({ length: previewData.num_columns }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>Columna {String.fromCharCode(65 + i)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tabla de preview con highlights de columnas mapeadas */}
              <div className="border border-neutral-200 rounded-sm overflow-auto max-h-[45vh]">
                <table className="w-full text-[11px] font-mono">
                  <thead className="bg-neutral-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left text-neutral-500 w-10">#</th>
                      {Array.from({ length: previewData.num_columns }, (_, i) => {
                        const roles = [];
                        if (previewData.mapping.fecha === i) roles.push({ label: "Fecha", cls: "bg-blue-100 text-blue-800" });
                        if (previewData.mapping.monto === i) roles.push({ label: "Monto", cls: "bg-emerald-100 text-emerald-800" });
                        if (previewData.mapping.descripcion === i) roles.push({ label: "Descripción", cls: "bg-amber-100 text-amber-800" });
                        if (previewData.mapping.numero === i) roles.push({ label: "Nº mov", cls: "bg-neutral-200 text-neutral-700" });
                        return (
                          <th key={i} className="px-2 py-1 text-left border-l border-neutral-200">
                            <div className="text-neutral-500">Col. {String.fromCharCode(65 + i)}</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {roles.map((r) => (
                                <span key={r.label} className={`text-[9px] px-1.5 py-0.5 rounded-sm font-semibold ${r.cls}`}>{r.label}</span>
                              ))}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((row, ridx) => {
                      const isHeader = ridx === previewData.mapping.header_row;
                      return (
                        <tr key={ridx} className={isHeader ? "bg-neutral-50 font-semibold" : (ridx % 2 === 0 ? "" : "bg-neutral-50/60")}>
                          <td className="px-2 py-1 text-neutral-400">
                            {ridx + 1}{isHeader ? " ⭐" : ""}
                          </td>
                          {Array.from({ length: previewData.num_columns }, (_, cidx) => {
                            let cls = "px-2 py-1 border-l border-neutral-200 truncate max-w-[240px]";
                            if (previewData.mapping.fecha === cidx) cls += " bg-blue-50";
                            else if (previewData.mapping.monto === cidx) cls += " bg-emerald-50";
                            else if (previewData.mapping.descripcion === cidx) cls += " bg-amber-50";
                            else if (previewData.mapping.numero === cidx) cls += " bg-neutral-100";
                            return <td key={cidx} className={cls}>{row[cidx] ?? ""}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="text-[11px] text-neutral-500">
                <b>Regla de signo:</b> los importes negativos se cargan como egresos y los positivos como ingresos.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPreviewData(null)}>Cancelar</Button>
            <Button
              className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
              onClick={confirmPreviewImport}
              disabled={importing}
              data-testid="preview-confirm-import"
            >
              {importing ? "Importando…" : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Arqueo de efectivo */}
      <Dialog open={arqueoOpen} onOpenChange={setArqueoOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">Arqueo de efectivo</DialogTitle>
          </DialogHeader>
          {selected?.tipo === "efectivo" && (
            <ArqueoView
              cajaMostrador={selected}
              standalone
              onDone={async () => {
                setArqueoOpen(false);
                await refreshAll();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Histórico de Arqueos */}
      <Dialog
        open={histArqueoOpen}
        onOpenChange={async (v) => {
          setHistArqueoOpen(v);
          if (v && selected?.id) {
            setHistLoading(true);
            try {
              const { data } = await api.get("/pos/arqueos", { params: { caja_id: selected.id, limit: 100 } });
              setHistArqueos(data || []);
            } catch (_) {
              setHistArqueos([]);
            } finally {
              setHistLoading(false);
            }
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise size={20} className="text-[#6B1F24]" />
              Historial de arqueos · {selected?.nombre}
            </DialogTitle>
          </DialogHeader>
          {histLoading ? (
            <div className="py-10 text-center text-[13px] text-[#999]">Cargando…</div>
          ) : histArqueos.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-[#999]">Aún no hay arqueos registrados para esta caja.</div>
          ) : (
            <div className="border border-[#E5E5E5] rounded-sm overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-neutral-50 border-b border-[#E5E5E5]">
                  <tr className="text-left text-[11px] uppercase font-mono text-[#666]">
                    <th className="px-4 py-2">Fecha</th>
                    <th className="px-4 py-2">Operador</th>
                    <th className="px-4 py-2 text-right">Sistema</th>
                    <th className="px-4 py-2 text-right">Contado</th>
                    <th className="px-4 py-2 text-right">Diferencia</th>
                    <th className="px-4 py-2">Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {histArqueos.map((a) => {
                    const dif = Number(a.diferencia || 0);
                    const difColor = Math.abs(dif) < 0.01
                      ? "text-neutral-500"
                      : dif > 0 ? "text-emerald-700" : "text-rose-700";
                    const fechaStr = (a.fecha || "").replace("T", " ").slice(0, 16);
                    return (
                      <tr key={a.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`arqueo-row-${a.id}`}>
                        <td className="px-4 py-2 font-mono text-[12px]">{fechaStr}</td>
                        <td className="px-4 py-2 text-[12px]">{a.operador_username || "—"}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmtMoney(a.total_sistema || 0)}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(a.total_contado || 0)}</td>
                        <td className={`px-4 py-2 text-right font-mono font-bold ${difColor}`}>
                          {dif > 0 ? "+" : ""}{fmtMoney(dif)}
                        </td>
                        <td className="px-4 py-2 text-[11px] text-neutral-600 truncate max-w-[220px]" title={a.notas || ""}>
                          {a.notas || <span className="text-[#ccc]">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
